import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { getPlatformClass } from '../utils/platformUtils';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './LanguageSwitcher';
import './MainHamburgerMenu.css';

const MainHamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { userRole } = useOrganization();
  const { t } = useTranslation('navigation');
  const { inventory: inventoryEnabled, receivables: receivablesEnabled } = useFeatureFlags();

  // Add error boundary for translation
  const safeTranslate = (key, options = {}) => {
    try {
      return t(key, options);
    } catch (error) {
      return key; // Return the key as fallback
    }
  };

  const handleMenuToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuClose = () => {
    setIsOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleMenuClose();
  };

  // Role-based menu items
  const getMenuItems = () => {
    const allMenuItems = [
      // Profile is always available
      { label: safeTranslate('menu.profile'), path: '/profile', icon: '👤', roles: ['*'] },
      // Account & Plan is only available to BusinessOwners
      { label: safeTranslate('menu.accountPlan'), path: '/account-plan', icon: '💳', roles: ['BusinessOwner'] },
    ];

    if (receivablesEnabled) {
      allMenuItems.push({
        label: safeTranslate('menu.receivables'),
        path: '/receivables',
        icon: '💰',
        roles: ['BusinessOwner', 'Accountant', 'Salesperson'],
      });
    }

    if (inventoryEnabled) {
      allMenuItems.push({
        label: safeTranslate('menu.inventory'),
        path: '/inventory',
        icon: '📦',
        roles: ['InventoryManager', 'BusinessOwner', 'Accountant', 'Salesperson'],
      });
    }

    // Filter all menu items based on user role
    const filteredMenuItems = allMenuItems.filter(item => 
      item.roles.includes(userRole) || item.roles.includes('*')
    );

    return filteredMenuItems;
  };

  const menuItems = getMenuItems();

  return (
    <>
      {/* Hamburger Button */}
      <button 
        className={`main-hamburger-button ${getPlatformClass()}`}
        onClick={handleMenuToggle}
        aria-label="Open menu"
      >
        <div className={`main-hamburger-icon ${isOpen ? 'open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>

      {/* Menu Overlay rendered via portal to document.body */}
      {isOpen && createPortal(
        <div className={`main-menu-overlay ${getPlatformClass()}`} onClick={handleMenuClose}>
          <div 
            className={`main-menu-container ${getPlatformClass()}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="main-menu-header">
              <h3 className="main-menu-title">{safeTranslate('menu.title')}</h3>
              <button 
                className="main-menu-close-button"
                onClick={handleMenuClose}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            {/* Menu Items */}
            <div className="main-menu-items">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  className="main-menu-item"
                  onClick={() => handleNavigation(item.path)}
                >
                  <span className="main-menu-item-icon">{item.icon}</span>
                  <span className="main-menu-item-label">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Language Switcher */}
            <div className="main-menu-language-section">
              <div className="main-menu-language-label">{safeTranslate('selectLanguage')}</div>
              <LanguageSwitcher variant="dropdown" showFlags={true} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MainHamburgerMenu; 